import { apiCoName, apiRealName } from '@/network/api/api';
import { CheckCircleFilled } from '@ant-design/icons';
import type { StepsProps } from 'antd';
import { Button, Flex, Form, Modal, Progress, Steps, message } from 'antd';
import { cloneDeep } from 'lodash';
import qs from 'qs';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BankInfoForm from './components/BankInfoForm';
import CompanyInfoForm from './components/CompanyInfoForm';
import ContactInfoForm from './components/ContactInfoForm';
import LegalPersonForm from './components/LegalPersonForm';
import ScenicInfoForm from './components/ScenicInfoForm';
import { ExpirationDateTypeEnum } from './constants';
import useRegisterProgress from './hooks/useRegisterProgress';
import './index.less';

// re-export for backward compatibility
export { ExpirationDateTypeEnum } from './constants';

const SCENIC_HOST = import.meta.env.VITE_SCENIC_HOST;
const DEFAULT_LOGO = import.meta.env.VITE_DEFAULT_LOGO;

const Page = () => {
  const [require, setRequire] = useState(true);
  const [require2, setRequire2] = useState(true);
  const [loadingReal, setLoadingReal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canGoLogin, setCanGoLogin] = useState(false);
  const [countdownTime, setCountdownTime] = useState(0);
  const [canSendOPT, setCanSendOPT] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [formRef] = Form.useForm();
  const formData = useRef<any[]>([]);

  const location = useLocation();
  const navigate = useNavigate();
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });
  const isCreateScenic = (search.type ?? 0) == 0;

  const { progressState, setProgressState, sseProgress } = useRegisterProgress(
    isCreateScenic,
    formData,
  );

  const customDot: StepsProps['progressDot'] = (dot, { status }) => {
    if (status === 'wait') {
      return <span className={'progress__dot'} />;
    } else {
      return <CheckCircleFilled style={{ fontSize: '22px', color: '#216EF3', margin: '0 0 0 -7px' }} />;
    }
  };

  const steps = [
    { title: '景区信息' },
    { title: '企业信息' },
    { title: '法人信息' },
    { title: '收款信息', pass: 1 },
    { title: '联系信息' },
  ];
  if (!isCreateScenic) {
    steps.splice(0, 1);
  }

  /**
   * 上一步按钮
   */
  const prevStep = () => {
    window.scrollTo(0, 0);
    if (currentStep == 0) {
      navigate(-1);
    } else {
      const step = currentStep - 1;
      formRef.setFieldsValue(formData.current[step]);
      setCurrentStep(step);
    }
  };

  /**
   * 下一步按钮
   */
  const nextStep = () => {
    window.scrollTo(0, 0);
    formRef.submit();
  };

  /**
   * 已经注册过可直接登录
   */
  const goLogin = () => {
    let redirection = import.meta.env.VITE_URL_EXCHANGE;
    if (isCreateScenic) {
      const uniqueIdentity = formRef.getFieldValue('uniqueIdentity');
      redirection = SCENIC_HOST + '/#/' + uniqueIdentity + '/';
    }
    window.location.href = redirection;
  };

  const onFormFinish: any = async (title?: string) => {
    console.log(formRef.getFieldsValue());
    if (title == '企业信息') {
      setLoadingReal(true);
      const hide = message.loading('企业名称校验中', 0);
      try {
        const { data }: any = await apiCoName(formRef.getFieldValue('coName'));
        hide();
        setLoadingReal(false);
        if (data) {
          message.success('企业名称校验成功');
        } else {
          Modal.error({ content: '企业名称重复' });
          return;
        }
      } catch (error) {
        hide();
        setLoadingReal(false);
        return;
      }
    }
    if (title == '法人信息') {
      const name = formRef.getFieldValue('legalRepresentativeName');
      const idCard = formRef.getFieldValue('documentNumber');
      if (!name || !idCard) {
        message.warning('请输入姓名与身份证号！');
        return;
      }
      setLoadingReal(true);
      const hide = message.loading('实名校验中', 0);
      try {
        const { data }: any = await apiRealName({ name, idCard });
        hide();
        setLoadingReal(false);
        if (data.code === 'SUCCESS') {
          message.success('实名校验成功');
        } else {
          Modal.error({
            title: '实名校验失败',
            content: '请检查法人姓名和证件号码是否正确',
          });
          return;
        }
      } catch (error) {
        hide();
        setLoadingReal(false);
        return;
      }
    }
    formData.current[currentStep] = cloneDeep(formRef.getFieldsValue());
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      submitSignUp();
    }
  };

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * 提交注册
   */
  const submitSignUp = async () => {
    let scenicInfo = formData.current[0];
    let companyInfo = formData.current[1];
    let leaderInfo = formData.current[2];
    let bankInfo = formData.current[3];
    let contactInfo = formData.current[4];

    if (!isCreateScenic) {
      companyInfo = formData.current[0];
      leaderInfo = formData.current[1];
      bankInfo = formData.current[2];
      contactInfo = formData.current[3];
    }

    let businessLicenseBeginDate = undefined;
    let businessLicenseEndDate = undefined;
    if (companyInfo.licenseExpirationDateType == ExpirationDateTypeEnum[1].value) {
      businessLicenseBeginDate = companyInfo.businessLicenseDate[0].format('YYYY-MM-DD');
      businessLicenseEndDate = companyInfo.businessLicenseDate[1].format('YYYY-MM-DD');
    }

    let documentBeginDate = undefined;
    let documentEndDate = undefined;
    if (leaderInfo.documentExpirationDateType == ExpirationDateTypeEnum[1].value) {
      documentBeginDate = leaderInfo.documentDate[0].format('YYYY-MM-DD');
      documentEndDate = leaderInfo.documentDate[1].format('YYYY-MM-DD');
    }

    const postForm: any = {
      appScenicPermissionDTO: {
        co: {
          coCode: companyInfo.coCode,
          coName: companyInfo.coName,
          isOTA: 0,
          contactName: contactInfo.contactName,
          contactPhone: contactInfo.contactPhone,
          licenseExpirationDateType: companyInfo.licenseExpirationDateType,
          businessLicenseBeginDate,
          businessLicenseEndDate,
          businessLicenseImageUrl: companyInfo.businessLicenseImageUrl,
          legalRepresentativeName: leaderInfo.legalRepresentativeName,
          documentType: leaderInfo.documentType,
          documentNumber: leaderInfo.documentNumber,
          documentExpirationDateType: leaderInfo.documentExpirationDateType,
          documentBeginDate,
          documentEndDate,
          documentImageUrl: leaderInfo.documentImageUrl.join(','),
        },
        isSale: true,
        relationCoAndType: [
          {
            type: isCreateScenic ? 0 : 1,
          },
        ],
        system: 1,
      },
      experienceInfoDTO: {
        autoRegister: true,
        credential: contactInfo.contactPhone,
        contractsName: contactInfo.contactName,
        loginTypeCode: 4,
        secret: contactInfo.secret,
      },
    };

    let coProvinceInfo: any = { coAddressInfo: companyInfo.coAddressInfo };
    if (companyInfo.coProvince != undefined) {
      coProvinceInfo = {
        ...coProvinceInfo,
        coProvinceName: companyInfo.coProvince.provinceName,
        coProvinceCode: companyInfo.coProvince.provinceCode,
        coCityName: companyInfo.coProvince.cityName,
        coCityCode: companyInfo.coProvince.cityCode,
        coAreaName: companyInfo.coProvince.areaName,
        coAreaCode: companyInfo.coProvince.areaCode,
      };
    }
    if (JSON.stringify(coProvinceInfo) != '{}') {
      postForm.appScenicPermissionDTO.coAddress = coProvinceInfo;
    }

    let redirection = import.meta.env.VITE_URL_EXCHANGE;
    if (isCreateScenic) {
      redirection = SCENIC_HOST + '/#/' + scenicInfo.uniqueIdentity + '/';

      let provinceInfo = {};
      if (scenicInfo.scenicProvince != undefined) {
        provinceInfo = {
          provinceName: scenicInfo.scenicProvince.provinceName,
          provinceCode: scenicInfo.scenicProvince.provinceCode,
          cityName: scenicInfo.scenicProvince.cityName,
          cityCode: scenicInfo.scenicProvince.cityCode,
          areaName: scenicInfo.scenicProvince.areaName,
          areaCode: scenicInfo.scenicProvince.areaCode,
        };
      }

      postForm.scenicExperienceDTO = {
        address: scenicInfo.address,
        appInfo: {
          appBrief: '基于联盟链的全场',
          applicationName: scenicInfo.scenicName + '景区管理系统',
          imgUrl: DEFAULT_LOGO,
          loginType: 2,
          logoutType: 1,
          type: 1,
          url: redirection,
          enterpriseId: 1,
        },
        ...provinceInfo,
        grade: scenicInfo.grade,
        scenicName: scenicInfo.scenicName,
        uniqueIdentity: scenicInfo.uniqueIdentity,
      };
    }

    if (bankInfo.account != undefined) {
      postForm.appScenicPermissionDTO.coBankAccount = {
        bankId: bankInfo.bankId,
        bankName: bankInfo.bankName,
        accountName: companyInfo.coName,
        account: bankInfo.account,
      };
    }

    setLoading(true);
    const sseCode = sseProgress();
    await delay(2000);
    try {
      let response = await fetch(`${import.meta.env.VITE_API_BASE}/co/scenic/personal/tryout?sseCode=${sseCode}`, {
        method: 'post',
        body: JSON.stringify({ ...postForm }),
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      console.log(data);

      setTimeout(() => {
        setLoading(false);
        if (data.code == 20000) {
          message.success('注册成功');
          window.location.href = redirection;
        } else {
          setProgressState({ title: '开始注册' });
          message.error(data.msg);
        }
      }, 500);
    } catch (e) {
      console.log(e);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  // 构建表单步骤
  const forms = [
    ...(isCreateScenic
      ? [
          <ScenicInfoForm
            key="scenic"
            formRef={formRef}
            formData={formData}
            currentStep={currentStep}
            onFinish={onFormFinish}
            setCanGoLogin={setCanGoLogin}
          />,
        ]
      : []),
    <CompanyInfoForm
      key="company"
      formRef={formRef}
      formData={formData}
      currentStep={currentStep}
      onFinish={onFormFinish}
      require={require}
      setRequire={setRequire}
      setCanGoLogin={setCanGoLogin}
    />,
    <LegalPersonForm
      key="legal"
      formRef={formRef}
      onFinish={onFormFinish}
      require2={require2}
      setRequire2={setRequire2}
    />,
    <BankInfoForm
      key="bank"
      formRef={formRef}
      formData={formData}
      isCreateScenic={isCreateScenic}
      onFinish={onFormFinish}
    />,
    <ContactInfoForm
      key="contact"
      formRef={formRef}
      onFinish={onFormFinish}
      canSendOPT={canSendOPT}
      setCanSendOPT={setCanSendOPT}
      countdownTime={countdownTime}
      setCountdownTime={setCountdownTime}
    />,
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="form">
      <div className="form__steps">
        <Steps current={currentStep} progressDot={customDot} items={steps} />
      </div>
      <div className="wrap">
        {steps[currentStep].pass == 1 ? (
          <div className="pass">
            <a style={{ color: '#216EF3' }} onClick={nextStep}>
              暂不开通，跳过
            </a>
          </div>
        ) : (
          <></>
        )}
        {loading ? (
          <div style={{ padding: '200px 100px' }}>
            <Progress
              percent={progressState.progree}
              showInfo={false}
              strokeColor={{ '0%': '#1677ff', '100%': '#1677ff' }}
            />
            <Flex justify="center">
              <span>{progressState.title}</span>
            </Flex>
          </div>
        ) : (
          forms[currentStep]
        )}
        {!loading && canGoLogin ? (
          <Flex justify="center" gap="large" style={{ marginTop: '36px' }}>
            <Button type="primary" onClick={goLogin}>
              去登录
            </Button>
          </Flex>
        ) : (
          <Flex justify="center" gap="large" style={{ marginTop: '36px' }}>
            {!loading && currentStep >= 0 && (
              <Button disabled={loadingReal} style={{ margin: '0 8px' }} onClick={prevStep}>
                上一步
              </Button>
            )}
            {!loading && currentStep < steps.length - 1 && (
              <Button disabled={loadingReal} type="primary" onClick={nextStep}>
                下一步
              </Button>
            )}
            {!loading && currentStep == steps.length - 1 && (
              <Button type="primary" onClick={nextStep}>
                提交
              </Button>
            )}
          </Flex>
        )}
      </div>
      <div className="footer">
        <a href="https://beian.miit.gov.cn/">
          © 2001-{new Date().getFullYear()}「环球数科集团」版权所有 | 粤 ICP 备 09156180 号
        </a>
      </div>
    </div>
  );
};
export default Page;
